import { usePortalDropdownPosition } from '@hooks/usePortalDropdownPosition'
import React, { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	DropdownChevron,
	DropdownMenu,
	DropdownOption,
	DropdownRoot,
	DropdownTrigger,
	DropdownValue,
} from './Dropdown.styles'

export interface DropdownOptionDef<T extends string> {
	value: T
	label: string
}

export interface DropdownProps<T extends string> {
	options: ReadonlyArray<DropdownOptionDef<T>>
	value: T
	onChange: (value: T) => void
	/** Names the control for assistive tech — the current option alone does not say what it picks. */
	'aria-label': string
	id?: string
	className?: string
	/** Set by `Field` when it shows an error: the trigger takes the danger border. */
	'aria-invalid'?: boolean
	/** Set by `Field`: the hint or error it announces. */
	'aria-describedby'?: string
}

/**
 * The app's single-choice menu: a field-shaped trigger showing the current
 * option, and a portaled option list under it on the popover layer — the
 * styled counterpart of a native select, so a filter row reads like the rest
 * of the form. Controlled through `value` / `onChange`. Arrow keys move the
 * highlight, Enter or Space picks, Escape closes; clicking away closes too.
 * It has no width of its own: a form column stretches it, a flex row sizes
 * it to its content; wrap it in a styled container for a min-width rather
 * than restyling the trigger.
 */
export function Dropdown<T extends string>({
	options,
	value,
	onChange,
	id,
	className,
	'aria-label': ariaLabel,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: DropdownProps<T>) {
	const [isOpen, setIsOpen] = useState(false)
	const [highlightedIndex, setHighlighted] = useState(0)
	// Options can shrink while open; the highlight never points past the end.
	const highlighted = Math.min(highlightedIndex, Math.max(0, options.length - 1))
	const rootRef = useRef<HTMLDivElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const coords = usePortalDropdownPosition(rootRef, isOpen)
	const listboxId = useId()

	const selectedIndex = options.findIndex(option => option.value === value)
	const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined

	const open = () => {
		setHighlighted(Math.max(0, selectedIndex))
		setIsOpen(true)
	}
	const close = () => setIsOpen(false)
	const pick = (option: DropdownOptionDef<T>) => {
		onChange(option.value)
		close()
	}

	useEffect(() => {
		if (!isOpen) return
		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as Node
			if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) close()
		}
		document.addEventListener('mousedown', handleMouseDown)
		return () => document.removeEventListener('mousedown', handleMouseDown)
	}, [isOpen])

	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		const last = options.length - 1
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				if (!isOpen) open()
				else setHighlighted(index => Math.min(index + 1, last))
				break
			case 'ArrowUp':
				event.preventDefault()
				if (!isOpen) open()
				else setHighlighted(index => Math.max(index - 1, 0))
				break
			case 'Home':
				if (!isOpen) return
				event.preventDefault()
				setHighlighted(0)
				break
			case 'End':
				if (!isOpen) return
				event.preventDefault()
				setHighlighted(last)
				break
			case 'Enter':
			case ' ':
				event.preventDefault()
				if (!isOpen) open()
				else if (options[highlighted]) pick(options[highlighted])
				break
			case 'Escape':
				if (!isOpen) return
				event.preventDefault()
				close()
				break
			case 'Tab':
				close()
				break
		}
	}

	const optionId = (index: number) => `${listboxId}-${index}`

	return (
		<DropdownRoot ref={rootRef} className={className}>
			<DropdownTrigger
				type="button"
				id={id}
				aria-label={ariaLabel}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={isOpen ? listboxId : undefined}
				aria-owns={isOpen ? listboxId : undefined}
				aria-activedescendant={isOpen ? optionId(highlighted) : undefined}
				aria-invalid={ariaInvalid || undefined}
				aria-describedby={ariaDescribedBy}
				onClick={() => (isOpen ? close() : open())}
				onKeyDown={handleKeyDown}
			>
				<DropdownValue>{selected?.label ?? ''}</DropdownValue>
				<DropdownChevron aria-hidden $open={isOpen}>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M4 6l4 4 4-4"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</DropdownChevron>
			</DropdownTrigger>
			{isOpen &&
				coords &&
				createPortal(
					<DropdownMenu
						ref={menuRef}
						id={listboxId}
						role="listbox"
						aria-label={ariaLabel}
						$top={coords.top}
						$left={coords.left}
						$width={coords.width}
					>
						{options.map((option, index) => (
							<DropdownOption
								key={option.value}
								id={optionId(index)}
								role="option"
								aria-selected={option.value === value}
								$selected={option.value === value}
								$highlighted={index === highlighted}
								onMouseEnter={() => setHighlighted(index)}
								onMouseDown={event => {
									event.preventDefault() // keep focus on the trigger
									pick(option)
								}}
							>
								{option.label}
							</DropdownOption>
						))}
					</DropdownMenu>,
					document.body,
				)}
		</DropdownRoot>
	)
}

export default Dropdown
